/**
 * What an account's email address is, as stored and as compared: **trimmed and lower-cased**
 * (`USR-015` AC-1).
 *
 * ## Why this is one rule in the contracts
 *
 * The user pool matches addresses case-sensitively. `auth-stack.ts` has never set
 * `signInCaseSensitive`, Cognito fixes that setting when a pool is created, and changing it in CDK
 * replaces the pool — production's is `RETAIN`, so the change would orphan every account. So the
 * pool's case rule is made irrelevant instead: nothing reaches it, or the profile store, with a
 * capital in the address.
 *
 * The rule used to live in about twenty places as `.trim().toLowerCase()`, and member sign-up was
 * the one door that only trimmed. Its rows held the address as typed — the index still found them,
 * because ElectroDB lower-cases key values, but every comparison against the stored attribute and
 * every Cognito call made with it saw the capitals — and somebody who signed up again with
 * different capitals got a second Cognito identity and a 500.
 *
 * Use {@link emailAddressField} at an HTTP boundary and {@link normaliseEmailAddress} anywhere an
 * address is about to be written, looked up or sent to the identity provider. Both say the same
 * thing; the field is the zod spelling of the function.
 *
 * ## What it is not for
 *
 * Addresses that name no account — an organizer's public attendee-contact address, a test-send
 * recipient, a CMS form's reply-to — keep the capitals somebody typed, because nothing matches on
 * them.
 */
import { z } from 'zod';
/** The longest address the platform accepts: RFC 5321's 64-octet local part, `@`, and 255. */
export const EMAIL_ADDRESS_MAX_LENGTH = 320;
/**
 * The one rule: surrounding whitespace removed and every letter lower-cased.
 *
 * Applied to the whole address, local part included. RFC 5321 lets a mail server treat the local
 * part as case-sensitive; no mail provider this platform's members use does, and one account per
 * mailbox is the product's rule (`USR-003` AC-7), not the RFC's.
 */
export function normaliseEmailAddress(value) {
    return value.trim().toLowerCase();
}
/**
 * An account address on a request body, normalised as it is parsed.
 *
 * zod applies `trim` and `toLowerCase` in order before `email`, so the shape check sees the address
 * that will be stored. A door with a tighter limit chains its own `.max()` — both checks apply.
 */
export const emailAddressField = z.string().trim().toLowerCase().email().max(EMAIL_ADDRESS_MAX_LENGTH);
//# sourceMappingURL=email-address.js.map